import { useState, useRef, useCallback } from "react";
import CourtPlayerMarkers from "../../features/match/court/components/CourtPlayerMarkers";
import CourtReadyOverlay from "../../features/match/court/components/CourtReadyOverlay";
import m from "../../features/match/court/styles/matchLiveVisual.module.css";

/**
 * Shared court interaction shell — mirrors Badminton Court interaction contract.
 * Sport modules supply SVG only. Badminton Court remains the gold-standard path.
 */
export default function SportCourtShell({
  sportId,
  courtPresentationId,
  PortraitSvg,
  LandscapeSvg,
  onScore,
  scoreA,
  scoreB,
  serving,
  endsSwapped,
  canScore,
  orientation,
  spectatorView,
  serveRound,
  teamA,
  teamB,
  matchReady,
  showServeDot,
  onSwapCourtEnds,
  onSwapDoublesPartners,
  onPickServe,
  serveDisplay = null,
  /** "right" | "left" from sport engine — tennis/PB/TT serve court */
  serveCourt = null,
  /** "serviceBoxes" (default) | "fixedEnds" (table tennis) */
  playerStance = "serviceBoxes",
  /** Doubles: which partner on the serving side holds serve */
  serverIndex = 0,
  /** Optional nodes rendered inside `.court-stage` (e.g. non-stretched HTML labels). */
  stageExtras = null,
  /** Tennis (etc.) presentation surface — forwarded to court SVG components. */
  courtColour = undefined,
}) {
  const [fx, setFx] = useState([]);
  const nid = useRef(0);
  const portrait = orientation !== "landscape";
  const vbW = portrait ? 200 : 439;
  const vbH = portrait ? 439 : 200;
  const topSide = endsSwapped ? "B" : "A";
  const bottomSide = endsSwapped ? "A" : "B";
  const leftSide = endsSwapped ? "B" : "A";
  const rightSide = endsSwapped ? "A" : "B";
  const tap = useCallback(
    (side, e) => {
      if (!canScore) return;
      const r = e.currentTarget.closest(".court-stage").getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const id = nid.current++;
      setFx((f) => [...f, { id, x, y, side }]);
      setTimeout(() => setFx((f) => f.filter((i) => i.id !== id)), 600);
      onScore(side);
    },
    [onScore, canScore]
  );
  const courtDimmed = spectatorView || (!canScore && !matchReady);
  return (
    <div
      className={`court-wrap court-orient-${portrait ? "portrait" : "landscape"}`}
      data-sport={sportId}
      data-court-presentation={courtPresentationId}
      data-court-colour={courtColour || undefined}
      style={{
        opacity: courtDimmed ? 0.85 : 1,
        pointerEvents: spectatorView ? "none" : "auto",
      }}
    >
      <div className="court-stage">
        {portrait ? (
          <PortraitSvg surface={courtColour} />
        ) : (
          <LandscapeSvg surface={courtColour} />
        )}
        {stageExtras}
        <CourtPlayerMarkers
          portrait={portrait}
          endsSwapped={endsSwapped}
          teamA={teamA}
          teamB={teamB}
          vbW={vbW}
          vbH={vbH}
          serving={serving}
          scoreA={typeof scoreA === "number" ? scoreA : 0}
          scoreB={typeof scoreB === "number" ? scoreB : 0}
          serveRound={serveRound}
          showServeMarker={showServeDot}
          serveCourt={serveCourt}
          playerStance={playerStance}
          serverIndex={serverIndex}
        />
        {matchReady &&
          onSwapCourtEnds &&
          onSwapDoublesPartners &&
          onPickServe && (
            <CourtReadyOverlay
              portrait={portrait}
              endsSwapped={endsSwapped}
              teamA={teamA}
              teamB={teamB}
              serving={serving}
              vbW={vbW}
              vbH={vbH}
              onSwapCourt={onSwapCourtEnds}
              onSwapPartners={onSwapDoublesPartners}
              onPickServe={onPickServe}
            />
          )}
        {portrait ? (
          <>
            <div
              className="ctap"
              style={{ top: 0, left: 0, right: 0, height: "50%" }}
              onClick={(e) => tap(topSide, e)}
              aria-label={`Score team ${topSide}`}
            />
            <div
              className="ctap"
              style={{ bottom: 0, left: 0, right: 0, height: "50%" }}
              onClick={(e) => tap(bottomSide, e)}
              aria-label={`Score team ${bottomSide}`}
            />
          </>
        ) : (
          <>
            <div
              className="ctap"
              style={{ top: 0, left: 0, bottom: 0, width: "50%" }}
              onClick={(e) => tap(leftSide, e)}
              aria-label={`Score team ${leftSide}`}
            />
            <div
              className="ctap"
              style={{ top: 0, right: 0, bottom: 0, width: "50%" }}
              onClick={(e) => tap(rightSide, e)}
              aria-label={`Score team ${rightSide}`}
            />
          </>
        )}
        {fx.map((f) => (
          <div key={f.id}>
            <div
              className="rpl"
              style={{
                left: f.x,
                top: f.y,
                background:
                  f.side === "A" ? "var(--tra-color-team-a)" : "var(--tra-color-team-b)",
              }}
            />
            <div
              className="ptt"
              style={{
                left: f.x - 14,
                top: f.y - 14,
                color:
                  f.side === "A" ? "var(--tra-color-team-a)" : "var(--tra-color-team-b)",
              }}
            >
              +1
            </div>
          </div>
        ))}
        {serveDisplay ? (
          <div className={m.courtServeChip} aria-live="polite">
            {serveDisplay}
          </div>
        ) : null}
        {spectatorView && (
          <div className="match-spec-locked">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                stroke="currentColor"
                strokeWidth="1.8"
              />
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
            </svg>
            <span className="match-spec-locked-txt">Spectator mode</span>
          </div>
        )}
      </div>
    </div>
  );
}
